/**
 * The single R3F canvas used by both the tutorial and live matches.
 *
 * Mounted once and kept alive across screens so the arena never has to be
 * rebuilt mid-flow. Frame pacing, DPR clamping and the tab-visibility throttle
 * all live here.
 */
import { Suspense, useEffect, useRef } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { Arena } from '../world/Arena';
import { Lighting } from '../world/Lighting';
import { Postfx } from '../world/Postfx';
import { OpponentMage } from '../entities/OpponentMage';
import { PlayerWand } from '../entities/PlayerWand';
import { MagicCircle } from '../vfx/MagicCircle';
import { Projectiles } from '../vfx/Projectiles';
import { Impacts } from '../vfx/Impacts';
import { GestureTrail } from '../vfx/GestureTrail';
import { FirstPersonRig } from '../camera/FirstPersonRig';
import { NetSync } from './NetSync';
import { scene as bus } from './sceneBus';
import { considerAutoDowngrade, useSettings } from '../gameplay/settingsStore';

/** Samples frame time, publishes fps, and downgrades quality if we fall behind. */
function FrameMonitor() {
  const frames = useRef(0);
  const elapsed = useRef(0);
  const lowStreak = useRef(0);

  useFrame((_, delta) => {
    frames.current++;
    elapsed.current += delta;
    if (elapsed.current < 1) return;
    const fps = frames.current / elapsed.current;
    bus.fps = fps;
    frames.current = 0;
    elapsed.current = 0;

    // Only act after several bad seconds in a row: a single hitch during a
    // spell burst is not a reason to drop the whole preset.
    if (fps < 32) {
      lowStreak.current++;
      if (lowStreak.current >= 4) {
        lowStreak.current = 0;
        considerAutoDowngrade(fps);
      }
    } else {
      lowStreak.current = 0;
    }
  });
  return null;
}

/** Applies the hit-stop / slow-motion time scale to the whole scene clock. */
function TimeScaler() {
  const { clock } = useThree();
  useFrame((_, delta) => {
    if (bus.hitStop > 0) {
      bus.hitStop = Math.max(0, bus.hitStop - delta);
    }
    void clock;
  });
  return null;
}

/** Names reported by the common software rasterisers. */
const SOFTWARE_RENDERERS = /swiftshader|llvmpipe|software|basic render|microsoft basic/i;

function RendererSetup() {
  const { gl } = useThree();
  const quality = useSettings((s) => s.quality);
  const software = useSettings((s) => s.softwareRenderer);
  const setSoftwareRenderer = useSettings((s) => s.setSoftwareRenderer);

  // Detect a software rasteriser once, from the driver's own report.
  useEffect(() => {
    try {
      const ctx = gl.getContext();
      const ext = ctx.getExtension('WEBGL_debug_renderer_info');
      const name = ext
        ? String(ctx.getParameter(ext.UNMASKED_RENDERER_WEBGL))
        : String(ctx.getParameter(ctx.RENDERER));
      if (SOFTWARE_RENDERERS.test(name)) {
        console.info(`[spell] software WebGL detected (${name}) — dropping to the low preset`);
        setSoftwareRenderer(true);
      }
    } catch {
      // Extension unavailable: assume hardware and let the fps monitor decide.
    }
  }, [gl, setSoftwareRenderer]);

  useEffect(() => {
    gl.toneMapping = THREE.ACESFilmicToneMapping;
    gl.toneMappingExposure = 1.05;
    gl.shadowMap.enabled = quality.shadows && !software;
    gl.shadowMap.type = THREE.PCFSoftShadowMap;
    gl.setPixelRatio(software ? 0.6 : Math.min(window.devicePixelRatio, quality.dprCap));
  }, [gl, quality, software]);

  return null;
}

export function GameCanvas({ interactive }: { interactive: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Stop rendering entirely when the tab is hidden.
  const frameloopRef = useRef<'always' | 'never'>('always');
  useEffect(() => {
    const onVisibility = () => {
      frameloopRef.current = document.hidden ? 'never' : 'always';
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  return (
    <Canvas
      id="spell-canvas"
      ref={canvasRef}
      dpr={[1, 2]}
      gl={{ antialias: false, powerPreference: 'high-performance', alpha: false, stencil: false }}
      camera={{ fov: 62, near: 0.05, far: 120, position: [0, 1.65, 7] }}
      style={{ pointerEvents: interactive ? 'auto' : 'none' }}
      shadows
    >
      <RendererSetup />
      <FrameMonitor />
      <TimeScaler />
      <FirstPersonRig />
      <NetSync />
      <Lighting />
      <Suspense fallback={null}>
        <Arena />
        <OpponentMage />
        <Projectiles />
        <Impacts />
        <PlayerWand />
        <MagicCircle />
        <GestureTrail />
      </Suspense>
      <Postfx />
    </Canvas>
  );
}
